import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useAPIQuery } from 'shared/api/APIQuery';
import { castAPIData } from 'shared/api/APISchema';
import { APIResponse, requestCarrierAPI } from 'shared/api/CarrierAPIClient';
import { requestCarrierAPIPublic } from 'shared/api/PublicCarrierAPIClient';
import {
  AcceptPublicOfferResponseDTO,
  acceptPublicOfferResponseSchema,
  DriverType,
  publicDriverAcceptResponseSchema,
  publicDriverInviteResponseSchema,
  PublicOfferAddDriverDTO,
  PublicOfferAssignResponseDTO,
  publicOfferAssignResponseSchema,
  PublicOfferDeclinePayloadDTO,
  PublicOfferDriverDTO,
  publicOfferDriverSchema,
  PublicOfferDTO,
  PublicOfferInviteDriverDTO,
  PublicOfferInviteResponseDTO,
  publicOfferSchema,
} from './PublicOfferDTO';

export function usePublicOfferAPI() {
  const queryClient = useQueryClient();

  return useMemo(() => {
    const invalidatePublicOfferCache = () => {
      void queryClient.invalidateQueries({ queryKey: ['public-offer'] });
    };

    return {
      invalidatePublicOfferCache,

      acceptPublicOffer: (
        guid: string,
        isFirstOffer: boolean,
      ): Promise<AcceptPublicOfferResponseDTO> => {
        return requestCarrierAPIPublic([
          'POST /internal/web/offers/aa/{guid}/accept/{?is_first_offer}',
          { guid, is_first_offer: isFirstOffer },
        ]).then(
          (response) => {
            invalidatePublicOfferCache();
            return castAPIData(acceptPublicOfferResponseSchema, response.data);
          },
          (error) => {
            invalidatePublicOfferCache();
            return Promise.reject(error);
          },
        );
      },

      declinePublicOffer: (
        guid: string,
        isFirstOffer: boolean,
        payload: PublicOfferDeclinePayloadDTO,
      ): Promise<APIResponse> => {
        return requestCarrierAPIPublic(
          [
            'POST /internal/web/offers/aa/{guid}/decline/{?is_first_offer}',
            { guid, is_first_offer: isFirstOffer },
          ],
          { json: payload },
        ).then(
          (response) => {
            invalidatePublicOfferCache();
            return response;
          },
          (error) => {
            invalidatePublicOfferCache();
            return Promise.reject(error);
          },
        );
      },

      assignDriver: (
        token: string,
        offerGUID: string,
        driverGUID: string,
        isNewDriver: boolean,
        isFirstOffer: boolean,
      ): Promise<PublicOfferAssignResponseDTO> => {
        return requestCarrierAPI(
          [
            'POST /internal/web/offers/{guid}/assign/{?new_driver,is_first_offer}',
            {
              guid: offerGUID,
              new_driver: isNewDriver,
              is_first_offer: isFirstOffer,
            },
          ],
          {
            json: { driver_guid: driverGUID },
            headers: { authorization: `Token ${token}` },
          },
        ).then(
          (response) => {
            invalidatePublicOfferCache();
            return castAPIData(publicOfferAssignResponseSchema, response.data);
          },
          (error) => {
            invalidatePublicOfferCache();
            return Promise.reject(error);
          },
        );
      },

      acceptJoinRequest: (guid: string, name: string): Promise<DriverType> => {
        return requestCarrierAPI(
          ['POST /internal/web/drivers/{guid}/accept/', { guid }],
          {
            json: { name },
          },
        ).then((res) => {
          invalidatePublicOfferCache();
          return castAPIData(publicDriverAcceptResponseSchema, res.data);
        });
      },

      addDriver: (
        token: string,
        data: PublicOfferAddDriverDTO,
      ): Promise<PublicOfferDriverDTO> => {
        return requestCarrierAPI('POST /internal/web/offers/drivers/', {
          json: data,
          headers: { authorization: `Token ${token}` },
        }).then((response) => {
          invalidatePublicOfferCache();
          return castAPIData(publicOfferDriverSchema, response.data);
        });
      },

      inviteDriver: (
        token: string,
        data: PublicOfferInviteDriverDTO,
      ): Promise<PublicOfferInviteResponseDTO> => {
        return requestCarrierAPI('POST /internal/web/offers/drivers/', {
          json: data,
          headers: { authorization: `Token ${token}` },
        }).then((response) => {
          return castAPIData(publicDriverInviteResponseSchema, response.data);
        });
      },
      inviteUser: (
        data: PublicOfferInviteDriverDTO,
      ): Promise<PublicOfferInviteResponseDTO> => {
        return requestCarrierAPI('POST /internal/web/users/', {
          json: { ...data, roles: ['driver'] },
        }).then((response) => {
          return castAPIData(publicDriverInviteResponseSchema, response.data);
        });
      },
    };
  }, [queryClient]);
}

export function usePublicOffer(guid: string) {
  return useAPIQuery(
    ['public-offer', guid],
    () =>
      requestCarrierAPIPublic<APIResponse<PublicOfferDTO>>([
        'GET /internal/web/offers/aa/{guid}/',
        { guid },
      ]),
    { schema: publicOfferSchema },
  );
}
